import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { Observable, Subject, combineLatest } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';

import { OrganizationDetails } from '@models/organization-details.model';
import { MIPS_BLOCK_REASONS } from '@constants/mips-block-reasons';
import { OrganizationAdminActions } from '@store/actions/organization-admin/organization-admin.actions';
import { selectOrganizations } from '@store/selectors/organization-admin/organization-admin.selector';
import { MipsSubmissionBlockActions } from '@store/actions/mips-submission-block/mips-submission-block.actions';
import { selectMipsSubmissionBlockSaving } from '@store/selectors/mips-submission-block/mips-submission-block.selectors';

@Component({
  selector: 'app-mips-submission-bulk-block-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatSelectModule,
    MatChipsModule,
    MatIconModule,
    NgxMatSelectSearchModule,
  ],
  templateUrl: './mips-submission-bulk-block-modal.component.html',
  styleUrl: './mips-submission-bulk-block-modal.component.css',
})
export class MipsSubmissionBulkBlockModalComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly dialogRef = inject(MatDialogRef<MipsSubmissionBulkBlockModalComponent>);
  private readonly destroy$ = new Subject<void>();

  readonly reasons = MIPS_BLOCK_REASONS;
  readonly orgSearchControl = new FormControl<string>('');
  readonly orgSelectControl = new FormControl<number[]>([], { nonNullable: true });

  allOrgs$ = this.store.select(selectOrganizations);
  filteredOrgs$: Observable<OrganizationDetails[]>;
  selectedOrgs$: Observable<OrganizationDetails[]>;
  saving$ = this.store.select(selectMipsSubmissionBlockSaving);

  selectedReasonCodes: string[] = [];

  constructor() {
    this.filteredOrgs$ = combineLatest([
      this.allOrgs$,
      this.orgSearchControl.valueChanges.pipe(startWith('')),
    ]).pipe(
      map(([orgs, searchTerm]) => {
        if (!orgs) return [];
        if (!searchTerm) return orgs;
        return orgs.filter((org) => org.name.toLowerCase().includes(searchTerm.toLowerCase()));
      })
    );

    this.selectedOrgs$ = combineLatest([
      this.allOrgs$,
      this.orgSelectControl.valueChanges.pipe(startWith(this.orgSelectControl.value)),
    ]).pipe(
      map(([orgs, selectedIds]) => orgs.filter((org) => selectedIds.includes(+org.id)))
    );
  }

  ngOnInit(): void {
    this.store.dispatch(OrganizationAdminActions.loadOrganizations());

    this.actions$
      .pipe(
        ofType(MipsSubmissionBlockActions.bulkBlockOrganizationsSuccess),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.dialogRef.close());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  removeOrg(organizationId: string): void {
    const remaining = this.orgSelectControl.value.filter((id) => id !== +organizationId);
    this.orgSelectControl.setValue(remaining);
  }

  isReasonSelected(code: string): boolean {
    return this.selectedReasonCodes.includes(code);
  }

  toggleReason(code: string, isChecked: boolean): void {
    this.selectedReasonCodes = isChecked
      ? [...this.selectedReasonCodes, code]
      : this.selectedReasonCodes.filter((existing) => existing !== code);
  }

  get isSaveEnabled(): boolean {
    return this.orgSelectControl.value.length > 0 && this.selectedReasonCodes.length > 0;
  }

  onSave(): void {
    if (!this.isSaveEnabled) return;
    this.store.dispatch(
      MipsSubmissionBlockActions.bulkBlockOrganizations({
        request: {
          organizationIds: this.orgSelectControl.value,
          reasonCodes: this.selectedReasonCodes,
        },
      })
    );
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
